import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect } from 'react';

import type { ReverseGeocodeEffectProps } from '.';

/**
 * React effect component that performs reverse geocoding using Google Maps API.
 * When the `position` prop changes, it fetches the address and calls `setAddress`.
 *
 * @param reverseGeocodeEffectProps - Props containing position and setAddress callback.
 * @returns null (no rendered output)
 */
export function ReverseGeocodeEffect(reverseGeocodeEffectProps: ReverseGeocodeEffectProps) {
  const { position, setAddress } = reverseGeocodeEffectProps;
  const geocodingLib = useMapsLibrary('geocoding');

  /**
   * Selects the most relevant address from geocoder results based on priority.
   *
   * @param results - Array of geocoder results.
   * @returns The formatted address string or null.
   */
  const getResultAddress = (results: google.maps.GeocoderResult[] | null) => {
    if (!results || results.length === 0) return;

    const priority: string[] = ['street_address', 'establishment', 'point_of_interest'];

    for (const type of priority) {
      const result = results.find((r) => r.types.includes(type));
      if (result) {
        return result.formatted_address;
      }
    }
    const localityResult = results.find((r) => r.types.includes('locality') && r.types.includes('political'));
    if (localityResult) {
      return localityResult.formatted_address;
    }

    return results[0]?.formatted_address || null;
  };

  useEffect(() => {
    if (!geocodingLib || !position) return;
    const geocoder = new geocodingLib.Geocoder();
    geocoder.geocode({ location: position }, (results, status) => {
      if (status === 'OK') {
        const address = getResultAddress(results);
        setAddress?.(address ?? null);
      } else {
        console.error('Reverse geocoding failed:', status);
      }
    });
  }, [geocodingLib, position]);
  return null;
}
